import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import {
  ArrowLeft, AlertTriangle, AlertOctagon, Info, Search, Download, FileDown,
  Check, CheckCheck, ChevronLeft, ChevronRight, Eye, MessageSquare, X, Filter,
  RefreshCw, Clock, FileSpreadsheet, User, Calendar, AlertCircle, Ban, Copy,
  RotateCcw, Loader2
} from 'lucide-react';

const SEVERITY_CONFIG = {
  blocker: { label: 'Bloqueante', color: 'bg-red-100 text-red-800 border-red-200', icon: AlertOctagon, description: 'Impide la importación del registro' },
  warning: { label: 'Advertencia', color: 'bg-amber-100 text-amber-800 border-amber-200', icon: AlertTriangle, description: 'Se importó con posibles problemas' },
  info: { label: 'Informativo', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Info, description: 'Información sobre el proceso' },
  rejected: { label: 'Rechazado', color: 'bg-red-100 text-red-700 border-red-200', icon: X, description: 'Registro rechazado por validación' },
  duplicate: { label: 'Duplicado', color: 'bg-purple-100 text-purple-800 border-purple-200', icon: Copy, description: 'Posible registro duplicado' },
  structure_change: { label: 'Cambio estructura', color: 'bg-orange-100 text-orange-800 border-orange-200', icon: AlertTriangle, description: 'Se detectó cambio en la estructura del archivo' },
  unrecognized: { label: 'No reconocido', color: 'bg-gray-100 text-gray-700 border-gray-200', icon: Info, description: 'Valor no reconocido en catálogo' },
};

const ERROR_TYPE_LABELS = {
  invalid_date: 'Fecha inválida',
  invalid_amount: 'Monto inválido',
  missing_required: 'Campo requerido faltante',
  merged_cells: 'Celdas combinadas',
  duplicate_header: 'Encabezado duplicado',
  invalid_format: 'Formato inválido',
  unrecognized_value: 'Valor no reconocido',
  structure_mismatch: 'Estructura diferente',
  data_type_error: 'Tipo de dato incorrecto',
  row_skipped: 'Fila omitida',
  connection_error: 'Error de conexión',
  rate_limit: 'Límite de tasa excedido',
};

function SeverityBadge({ severity }) {
  const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.info;
  const Icon = config.icon;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge className={`text-[10px] gap-0.5 border ${config.color}`} variant="outline">
            <Icon className="w-2.5 h-2.5" /> {config.label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent><p className="text-xs">{config.description}</p></TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function formatDateTime(isoString) {
  if (!isoString) return '—';
  try {
    const date = new Date(isoString);
    return date.toLocaleString('es-MX', { 
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return isoString.slice(0, 16); }
}

export default function SyncErrorsPanel({ onBack }) {
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({ errors: [], total: 0, page: 1, total_pages: 1 });
  const [stats, setStats] = useState(null);
  const [filterOptions, setFilterOptions] = useState({});
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedError, setSelectedError] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewStatus, setReviewStatus] = useState('reviewed');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 25, search: search || undefined, ...filters };
      const [errRes, statsRes] = await Promise.all([
        api.get('/facturacion/errors', { params }),
        api.get('/facturacion/errors/stats', { params: filters.sync_id ? { sync_id: filters.sync_id } : {} }),
      ]);
      setErrors(errRes.data);
      setStats(statsRes.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [page, search, filters]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    api.get('/facturacion/errors/filter-options').then(r => setFilterOptions(r.data)).catch(() => {});
  }, []);

  const reviewError = async (errorId) => {
    try {
      await api.put(`/facturacion/errors/${errorId}/review`, {
        comment: reviewComment, 
        status: reviewStatus
      });
      toast.success('Error marcado como revisado');
      setReviewOpen(false);
      setReviewComment('');
      setReviewStatus('reviewed');
      loadData();
    } catch { toast.error('Error al marcar como revisado'); }
  };

  const bulkReview = async () => {
    if (selectedIds.size === 0) return;
    try {
      await api.post('/facturacion/errors/bulk-review', {
        error_ids: [...selectedIds], comment: 'Revisión masiva'
      });
      toast.success(`${selectedIds.size} errores marcados como revisados`);
      setSelectedIds(new Set());
      loadData();
    } catch { toast.error('Error en revisión masiva'); }
  };

  const handleRetry = async () => {
    // This is disabled until Google connection is active
    toast.info('Reintento no disponible. Pendiente de conexión con Google.');
  };

  const exportFile = async (type) => {
    setExporting(true);
    try {
      const params = { ...filters };
      const res = await api.get(`/facturacion/errors/export/${type}`, { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `errores_sync.${type === 'excel' ? 'xlsx' : 'csv'}`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Reporte ${type.toUpperCase()} descargado`);
    } catch { toast.error('Error al exportar'); }
    finally { setExporting(false); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === errors.errors.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(errors.errors.map(e => e.id)));
    }
  };

  const clearFilters = () => { setFilters({}); setSearch(''); setPage(1); };

  const hasActiveFilters = Object.values(filters).some(v => v) || search;

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="sync-errors-panel">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} className="h-8 w-8" data-testid="errors-back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Errores de Sincronización
          </h2>
          <p className="text-xs text-muted-foreground">
            Revisa y gestiona errores detectados durante la importación de Google Sheets
          </p>
        </div>
        <div className="flex gap-2">
          {selectedIds.size > 0 && (
            <Button size="sm" onClick={bulkReview} className="gap-1.5 text-xs" data-testid="bulk-review-btn">
              <CheckCheck className="w-3.5 h-3.5" /> Revisar ({selectedIds.size})
            </Button>
          )}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" disabled className="gap-1.5 text-xs opacity-50" data-testid="retry-btn">
                  <RotateCcw className="w-3.5 h-3.5" /> Reintentar
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Pendiente de conexión Google</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <div className="relative group">
            <Button variant="outline" size="sm" disabled={exporting} className="gap-1.5 text-xs">
              <Download className="w-3.5 h-3.5" /> Exportar
            </Button>
            <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg py-1 z-50 min-w-[140px] hidden group-hover:block">
              <button className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted flex items-center gap-2"
                onClick={() => exportFile('excel')} data-testid="export-excel-btn">
                <FileDown className="w-3.5 h-3.5" /> Excel (.xlsx)
              </button>
              <button className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted flex items-center gap-2"
                onClick={() => exportFile('csv')} data-testid="export-csv-btn">
                <FileDown className="w-3.5 h-3.5" /> CSV
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Connection Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-amber-800">
            <p className="font-medium">Pendiente de conexión Google</p>
            <p className="mt-0.5">
              Los errores se registrarán automáticamente cuando se active la sincronización con Google Sheets.
              Actualmente se muestran datos de ejemplo para demostrar la funcionalidad.
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-[10px] text-muted-foreground">Total errores</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
              <p className="text-[10px] text-muted-foreground">Pendientes</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-emerald-600">{stats.reviewed}</p>
              <p className="text-[10px] text-muted-foreground">Revisados</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-red-600">{stats.by_severity?.blocker?.count || 0}</p>
              <p className="text-[10px] text-muted-foreground">Bloqueantes</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input placeholder="Buscar en descripción, campo, valor..." className="h-8 text-xs pl-8"
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            data-testid="search-errors-input" />
        </div>
        <Select value={filters.severity || 'all'} onValueChange={v => setFilters(f => ({...f, severity: v === 'all' ? undefined : v}))}>
          <SelectTrigger className="h-8 text-xs w-[130px]" data-testid="filter-severity">
            <SelectValue placeholder="Severidad" />
          </SelectTrigger>
          <SelectContent position="popper">
            <SelectItem value="all">Todas</SelectItem>
            {(filterOptions.severities || Object.keys(SEVERITY_CONFIG)).map(s => (
              <SelectItem key={s} value={s}>{SEVERITY_CONFIG[s]?.label || s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.error_type || 'all'} onValueChange={v => setFilters(f => ({...f, error_type: v === 'all' ? undefined : v}))}>
          <SelectTrigger className="h-8 text-xs w-[140px]" data-testid="filter-error-type">
            <SelectValue placeholder="Tipo error" />
          </SelectTrigger>
          <SelectContent position="popper">
            <SelectItem value="all">Todos</SelectItem>
            {(filterOptions.error_types || Object.keys(ERROR_TYPE_LABELS)).map(t => (
              <SelectItem key={t} value={t}>{ERROR_TYPE_LABELS[t] || t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.review_status || 'all'} onValueChange={v => setFilters(f => ({...f, review_status: v === 'all' ? undefined : v}))}>
          <SelectTrigger className="h-8 text-xs w-[120px]" data-testid="filter-review-status">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent position="popper">
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="pending">Pendientes</SelectItem>
            <SelectItem value="reviewed">Revisados</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.sheet || 'all'} onValueChange={v => setFilters(f => ({...f, sheet: v === 'all' ? undefined : v}))}>
          <SelectTrigger className="h-8 text-xs w-[120px]" data-testid="filter-sheet">
            <SelectValue placeholder="Pestaña" />
          </SelectTrigger>
          <SelectContent position="popper">
            <SelectItem value="all">Todas</SelectItem>
            {(filterOptions.sheets || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs gap-1" data-testid="clear-filters-btn">
            <X className="w-3 h-3" /> Limpiar
          </Button>
        )}
      </div>

      {/* Errors Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading && !errors.errors.length ? (
            <div className="p-4 space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-10" />)}</div>
          ) : (
            <table className="w-full text-xs" data-testid="errors-table">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="p-2 w-8">
                    <input type="checkbox" className="rounded" 
                      checked={selectedIds.size === errors.errors.length && errors.errors.length > 0}
                      onChange={selectAll} />
                  </th>
                  <th className="text-left p-2 font-medium">Fecha</th>
                  <th className="text-left p-2 font-medium">Severidad</th>
                  <th className="text-left p-2 font-medium hidden sm:table-cell">Pestaña</th>
                  <th className="text-left p-2 font-medium hidden md:table-cell">Fila</th>
                  <th className="text-left p-2 font-medium">Descripción</th>
                  <th className="text-left p-2 font-medium hidden lg:table-cell">Campo</th>
                  <th className="text-left p-2 font-medium hidden xl:table-cell">Valor</th>
                  <th className="text-center p-2 font-medium">Estado</th>
                  <th className="text-center p-2 font-medium w-20">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {errors.errors.map(err => (
                  <tr key={err.id} className={`border-b hover:bg-muted/30 transition-colors ${err.reviewed ? 'opacity-60' : ''}`}>
                    <td className="p-2">
                      <input type="checkbox" className="rounded" 
                        checked={selectedIds.has(err.id)} 
                        onChange={() => toggleSelect(err.id)} />
                    </td>
                    <td className="p-2 text-muted-foreground whitespace-nowrap">
                      {formatDateTime(err.created_at)}
                    </td>
                    <td className="p-2"><SeverityBadge severity={err.severity} /></td>
                    <td className="p-2 hidden sm:table-cell">
                      <span className="text-muted-foreground flex items-center gap-1">
                        <FileSpreadsheet className="w-3 h-3" />
                        {err.sheet_name || '—'}
                      </span>
                    </td>
                    <td className="p-2 hidden md:table-cell text-muted-foreground font-mono">
                      {err.row_number || err.row_range || '—'}
                    </td>
                    <td className="p-2 max-w-[250px]">
                      <p className="truncate">{err.message}</p>
                      {err.error_type && (
                        <span className="text-[10px] text-muted-foreground">
                          {ERROR_TYPE_LABELS[err.error_type] || err.error_type}
                        </span>
                      )}
                    </td>
                    <td className="p-2 hidden lg:table-cell text-muted-foreground">
                      {err.field_name || '—'}
                    </td>
                    <td className="p-2 hidden xl:table-cell">
                      {err.original_value ? (
                        <code className="text-[10px] bg-muted px-1 py-0.5 rounded max-w-[100px] truncate block">
                          {String(err.original_value).slice(0, 30)}
                        </code>
                      ) : '—'}
                    </td>
                    <td className="p-2 text-center">
                      {err.reviewed ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                                <Check className="w-2.5 h-2.5 mr-0.5" /> Revisado
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs">{err.reviewed_by_name} · {formatDateTime(err.reviewed_at)}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                          <Clock className="w-2.5 h-2.5 mr-0.5" /> Pendiente
                        </Badge>
                      )}
                    </td>
                    <td className="p-2">
                      <div className="flex gap-1 justify-center">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-6 w-6" 
                                onClick={() => { setSelectedError(err); setDetailOpen(true); }}
                                data-testid={`view-error-${err.id}`}>
                                <Eye className="w-3 h-3" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent><p className="text-xs">Ver detalle</p></TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        {!err.reviewed && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-6 w-6" 
                                  onClick={() => { setSelectedError(err); setReviewOpen(true); }}
                                  data-testid={`review-error-${err.id}`}>
                                  <Check className="w-3 h-3" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent><p className="text-xs">Marcar revisado</p></TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!errors.errors.length && (
                  <tr>
                    <td colSpan={10} className="p-8 text-center text-muted-foreground">
                      {stats?.total === 0 
                        ? 'No hay errores registrados. Los errores aparecerán después de ejecutar una sincronización.' 
                        : 'No se encontraron errores con estos filtros.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
          {errors.total_pages > 1 && (
            <div className="flex items-center justify-between px-4 py-2 border-t">
              <p className="text-xs text-muted-foreground">
                Página {errors.page} de {errors.total_pages} ({errors.total} total)
              </p>
              <div className="flex gap-1">
                <Button variant="outline" size="icon" className="h-7 w-7" 
                  disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft className="w-3.5 h-3.5" />
                </Button>
                <Button variant="outline" size="icon" className="h-7 w-7" 
                  disabled={page >= errors.total_pages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" /> Detalle del Error
            </DialogTitle>
          </DialogHeader>
          {selectedError && (
            <div className="space-y-4 text-sm">
              <div className="flex gap-2 flex-wrap">
                <SeverityBadge severity={selectedError.severity} />
                {selectedError.error_type && (
                  <Badge variant="outline" className="text-[10px]">
                    {ERROR_TYPE_LABELS[selectedError.error_type] || selectedError.error_type}
                  </Badge>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="space-y-0.5">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Fecha
                  </span>
                  <p className="font-medium">{formatDateTime(selectedError.created_at)}</p>
                </div>
                <div className="space-y-0.5">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <FileSpreadsheet className="w-3 h-3" /> Archivo
                  </span>
                  <p className="font-medium">{selectedError.file_name || 'Google Sheet'}</p>
                </div>
                <div className="space-y-0.5">
                  <span className="text-muted-foreground">Pestaña</span>
                  <p className="font-medium">{selectedError.sheet_name || '—'}</p>
                </div>
                <div className="space-y-0.5">
                  <span className="text-muted-foreground">Fila / Rango</span>
                  <p className="font-medium font-mono">{selectedError.row_number || selectedError.row_range || '—'}</p>
                </div>
                <div className="space-y-0.5">
                  <span className="text-muted-foreground">Campo</span>
                  <p className="font-medium">{selectedError.field_name || '—'}</p>
                </div>
                <div className="space-y-0.5">
                  <span className="text-muted-foreground">Tipo de error</span>
                  <p className="font-medium">{ERROR_TYPE_LABELS[selectedError.error_type] || selectedError.error_type || '—'}</p>
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-1">
                <span className="text-muted-foreground text-xs">Descripción</span>
                <p className="p-2 bg-muted/50 rounded-lg">{selectedError.message}</p>
              </div>
              
              {selectedError.original_value && (
                <div className="space-y-1">
                  <span className="text-muted-foreground text-xs">Valor original</span>
                  <code className="block p-2 bg-gray-900 text-gray-100 rounded-lg text-xs font-mono whitespace-pre-wrap">
                    {String(selectedError.original_value)}
                  </code>
                </div>
              )}
              
              {selectedError.recommended_action && (
                <div className="space-y-1">
                  <span className="text-muted-foreground text-xs">Acción recomendada</span>
                  <p className="p-2 bg-blue-50 border border-blue-200 rounded-lg text-blue-800">
                    {selectedError.recommended_action}
                  </p>
                </div>
              )}
              
              {selectedError.reviewed && (
                <>
                  <Separator />
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Check className="w-4 h-4 text-emerald-600" />
                      <span className="text-xs font-medium text-emerald-700">Revisado</span>
                    </div>
                    <p className="text-xs text-emerald-600">
                      <User className="w-3 h-3 inline mr-1" />
                      {selectedError.reviewed_by_name} · {formatDateTime(selectedError.reviewed_at)}
                    </p>
                    {selectedError.review_comment && (
                      <p className="text-xs mt-2 italic text-emerald-700">
                        &ldquo;{selectedError.review_comment}&rdquo;
                      </p>
                    )}
                  </div>
                </>
              )}
              
              {/* Retry button in detail view */}
              {!selectedError.reviewed && (
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className="flex-1 gap-1.5 opacity-50" disabled
                    data-testid="detail-retry-btn">
                    <RotateCcw className="w-3.5 h-3.5" /> Reintentar fila
                    <span className="text-[10px] text-muted-foreground">(Pendiente conexión)</span>
                  </Button>
                  <Button className="flex-1 gap-1.5" 
                    onClick={() => { setDetailOpen(false); setReviewOpen(true); }}>
                    <Check className="w-3.5 h-3.5" /> Marcar revisado
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Marcar como revisado</DialogTitle>
            <DialogDescription>
              Indica el estado de la revisión y agrega un comentario opcional
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">
                Estado de revisión
              </label>
              <Select value={reviewStatus} onValueChange={setReviewStatus}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper">
                  <SelectItem value="reviewed">Revisado - Sin acción</SelectItem>
                  <SelectItem value="fixed_in_source">Corregido en origen</SelectItem>
                  <SelectItem value="ignored">Ignorado intencionalmente</SelectItem>
                  <SelectItem value="pending_fix">Pendiente de corrección</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">
                Comentario de revisión
              </label>
              <Textarea 
                className="min-h-[80px] text-sm resize-none"
                placeholder="Describe la acción tomada o por qué se marca como revisado..."
                value={reviewComment}
                onChange={e => setReviewComment(e.target.value)}
                data-testid="review-comment-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)}>Cancelar</Button>
            <Button onClick={() => selectedError && reviewError(selectedError.id)} className="gap-1.5"
              data-testid="confirm-review-btn">
              <Check className="w-3.5 h-3.5" /> Marcar revisado
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
